import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getBrowserTabShot } from '../transport/relay'
import { busyVerdict } from '../agents/useIdleStatus'
import { onEdgeEvent } from '../servers/commandStream'

/** Where a picture is kept while something shows it — the one query both of its users ask by. */
export const tabPictureKey = (browserId: string, tab: string, fit: number, url: boolean) => ['tab-shot', browserId, tab, fit, url] as const

/**
 * A PICTURE OF A HELD TAB — the sheet's (TabShotSheet) and the one in the corner of its agent's
 * session (AgentTabCorner). Asked of the browser that holds the tab (extension 0.2.22+), `fit`
 * device pixels wide, and kept nowhere but in the one that asked: a picture is of a moment.
 * Taken when first wanted, and again when `takeAgain` is called (useRetakeMoments says when).
 * A reason that lands while a picture is being taken is owed — for THAT tab: a different one
 * asked for next starts clean (the review of 2026-10-15). Nothing is taken while `enabled` is
 * false.
 *
 * `auto: false` — taken ONLY when `takeAgain` is called, never because the picture is wanted:
 * a query with no picture yet fetches itself whenever `enabled` turns true, and for the corner
 * that was a sheet closing or moving to another tab, neither of them a moment it may take one
 * at (the review of 2026-10-15, second round).
 */
export function useTabPicture({
  browserId,
  tab,
  fit,
  url = false,
  enabled,
  auto = true,
}: {
  browserId: string
  tab: string
  fit: number
  /** With the page's address (0.2.23+), for "Open page"; never asked for when nothing shows it. */
  url?: boolean
  enabled: boolean
  auto?: boolean
}) {
  const shot = useQuery({
    queryKey: tabPictureKey(browserId, tab, fit, url),
    queryFn: () => getBrowserTabShot({ browser_id: browserId, tab, fit, ...(url ? { url: true } : {}) }),
    enabled: auto && enabled,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  const live = useRef(true)
  const owed = useRef(false)
  useEffect(() => {
    live.current = true // (StrictMode runs the cleanup once on mount: set it again here)
    return () => {
      live.current = false
    }
  }, [])
  useEffect(() => {
    owed.current = false
  }, [browserId, tab, fit, url])
  const takeAgain = () => {
    if (!live.current || !enabled) return
    if (shot.isFetching) owed.current = true
    else void shot.refetch()
  }
  const takeAgainRef = useRef(takeAgain)
  takeAgainRef.current = takeAgain
  useEffect(() => {
    if (shot.isFetching || !owed.current) return
    owed.current = false
    takeAgainRef.current()
  }, [shot.isFetching])

  return { shot, takeAgain }
}

/**
 * WHEN A PICTURE OF A HELD TAB IS TAKEN AGAIN — never on a clock (no timer asks the same
 * question again: the owner's standing rule, noHeartbeat.test.ts):
 *
 *  · WHEN THE AGENT FINISHES ITS TURN — busy to idle, the edge's own verdict and push, and when
 *    what the agent was told shows on the page. Seeded from the switch the app already holds,
 *    so a picture wanted mid-turn still catches the end of that turn. Not while the app is
 *    hidden: nobody sees that picture, and coming back takes one.
 *  · WHEN THE PERSON COMES BACK — a phone unlocked or a browser tab switched back to
 *    (visibilitychange), and the Mac app clicked again beside Chrome: a window that was never
 *    hidden says only `focus`, and TanStack's own refetch-on-focus listens for the other alone
 *    (the review of 2026-10-14). `focusAwayMs` is how long the window must have been away for a
 *    focus to count: the corner, on screen all along, takes none for a click between split
 *    panes or a glance at Chrome (the review of 2026-10-15). A browser tab coming back says
 *    both at once: one moment for the pair.
 */
export function useRetakeMoments({
  device,
  session,
  focusAwayMs = 0,
  onMoment,
}: {
  /** The agent holding the tab: the machine it is on, and its session there. */
  device: string
  session: string
  focusAwayMs?: number
  onMoment: () => void
}) {
  const moment = useRef(onMoment)
  moment.current = onMoment

  useEffect(() => {
    let wasBusy = busyVerdict(device, session) === true
    return onEdgeEvent((event, data) => {
      if (event !== 'idle_status' || data['device_id'] !== device) return
      const busy = Array.isArray(data['busy']) && (data['busy'] as unknown[]).includes(session)
      if (wasBusy && !busy && document.visibilityState !== 'hidden') moment.current()
      wasBusy = busy
    })
  }, [device, session])

  useEffect(() => {
    let backAt = 0
    let awayAt = 0
    const back = () => {
      const now = Date.now()
      if (now - backAt < 1000) return
      backAt = now
      moment.current()
    }
    const shown = () => {
      if (document.visibilityState !== 'hidden') back()
    }
    const focused = () => {
      if (document.visibilityState === 'hidden') return
      if (focusAwayMs > 0 && (!awayAt || Date.now() - awayAt < focusAwayMs)) return
      back()
    }
    const away = () => {
      awayAt = Date.now()
    }
    window.addEventListener('focus', focused)
    window.addEventListener('blur', away)
    document.addEventListener('visibilitychange', shown)
    return () => {
      window.removeEventListener('focus', focused)
      window.removeEventListener('blur', away)
      document.removeEventListener('visibilitychange', shown)
    }
  }, [focusAwayMs])
}
